import {
  ADDON_DWH_ALIAS,
  ADDON_DWH_RENT_ALIAS,
  ADDON_DWH_RENT_PRICE_PER_ROW,
  ADDON_DWH_YEARLY_ALIAS,
  ADDON_METABASE_AI_MANAGED_PRICE_PER_TOKEN,
  ADDON_PRODUCT_DEPLOYMENT_ALL_TYPE,
  ADDON_PRODUCT_DEPLOYMENT_CLOUD_TYPE,
  ADDON_PRODUCT_DEPLOYMENT_SELF_HOSTED_TYPE,
  ADDON_PRODUCT_ANALYTICS_ALIAS,
  ADDON_PRODUCT_ANALYTICS_PRICE_PER_EVENT,
  ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE,
  ADDON_PRODUCT_METABASE_AI_TYPE,
} from "./constants";
import { IAddon, IFilterAddOnsProps } from "./types";

function isFreeAddOn(alias: string): boolean {
  return alias.endsWith("-free");
}

function isYearlyAddOn(alias: string): boolean {
  return alias.includes("-yearly");
}

// legacy prepaid Storage only — must not match "dwh-rent"
function isDWHAddOn(alias: string): boolean {
  return alias === ADDON_DWH_ALIAS || alias === ADDON_DWH_YEARLY_ALIAS;
}

export function isDWHRentAddOn(alias: string): boolean {
  return alias === ADDON_DWH_RENT_ALIAS;
}

// Paid Metabase Track only. The free variant is `self_service: false` in
// harbormaster, so it never reaches us through `/addons`.
export function isProductAnalyticsAddOn(alias: string): boolean {
  return alias === ADDON_PRODUCT_ANALYTICS_ALIAS;
}

export function getProductAnalyticsPricePerEvent(
  pricePerUnit: number | null | undefined,
): number {
  return pricePerUnit || ADDON_PRODUCT_ANALYTICS_PRICE_PER_EVENT;
}

// Every price we quote for Track is per 1M events; the API rate is per event.
export function getProductAnalyticsPricePer1M(
  pricePerUnit: number | null | undefined,
): number {
  return 1_000_000 * getProductAnalyticsPricePerEvent(pricePerUnit);
}

export function getMetabaseAIManagedPricePerToken(
  pricePerUnit: number | null | undefined,
): number {
  return pricePerUnit || ADDON_METABASE_AI_MANAGED_PRICE_PER_TOKEN;
}

export function getStorageRentPricePerRow(
  pricePerUnit: number | null | undefined,
): number {
  return pricePerUnit ?? ADDON_DWH_RENT_PRICE_PER_ROW;
}

export function isTransformsAddOn(alias: string): boolean {
  return alias.includes("transforms-");
}

function isAIAddOn(alias: string): boolean {
  return alias.endsWith("-ai") || alias.includes("-ai-");
}

export function isFreeAIAddOn(alias: string): boolean {
  return alias === ADDON_PRODUCT_METABASE_AI_TYPE;
}

export function isManagedAIAddOn(alias: string): boolean {
  return alias === ADDON_PRODUCT_METABASE_AI_MANAGED_TYPE;
}

export function filterAddOns(
  addOns: IAddon[],
  {
    type: filterProductType,
    yearly: filterYearly,
    cloud: filterCloud,
    selfHosted: filterSelfHosted,
    metered: filterMetered,
    dwh: filterDWH,
    dwhRent: filterDWHRent,
    transforms: filterTransforms,
    ai: filterAI,
    free: filterFree,
  }: IFilterAddOnsProps = {},
): IAddon[] {
  const filteredAddOns = addOns
    // remove free add-ons
    .filter(({ product_type }) => !isFreeAddOn(product_type))
    // type
    .filter(({ product_type }) => {
      if (filterProductType === undefined) {
        return true;
      }

      return filterProductType === product_type;
    })
    // yearly
    .filter(({ alias, is_metered }) => {
      if (is_metered || filterYearly === undefined) {
        return true;
      }

      return filterYearly ? isYearlyAddOn(alias) : !isYearlyAddOn(alias);
    })
    // deployment
    .filter(({ deployment }) => {
      if (filterCloud === undefined) {
        return true;
      }
      // false
      else if (!filterCloud) {
        return (
          deployment === ADDON_PRODUCT_DEPLOYMENT_ALL_TYPE ||
          deployment === ADDON_PRODUCT_DEPLOYMENT_SELF_HOSTED_TYPE
        );
      }

      // true
      return (
        deployment === ADDON_PRODUCT_DEPLOYMENT_ALL_TYPE ||
        deployment === ADDON_PRODUCT_DEPLOYMENT_CLOUD_TYPE
      );
    })
    .filter(({ deployment }) => {
      if (filterSelfHosted === undefined) {
        return true;
      }
      // false
      else if (!filterSelfHosted) {
        return (
          deployment === ADDON_PRODUCT_DEPLOYMENT_ALL_TYPE ||
          deployment === ADDON_PRODUCT_DEPLOYMENT_CLOUD_TYPE
        );
      }

      // true
      return (
        deployment === ADDON_PRODUCT_DEPLOYMENT_ALL_TYPE ||
        deployment === ADDON_PRODUCT_DEPLOYMENT_SELF_HOSTED_TYPE
      );
    })
    .filter(({ is_metered }) => {
      if (filterMetered === undefined) {
        return true;
      }

      return filterMetered === is_metered;
    })
    // add-ons
    .filter(({ alias }) => {
      if (filterDWH === undefined) {
        return true;
      }

      return filterDWH ? isDWHAddOn(alias) : !isDWHAddOn(alias);
    })
    .filter(({ alias }) => {
      if (filterDWHRent === undefined) {
        return true;
      }

      return filterDWHRent ? isDWHRentAddOn(alias) : !isDWHRentAddOn(alias);
    })
    .filter(({ alias }) => {
      if (filterTransforms === undefined) {
        return true;
      }

      return filterTransforms
        ? isTransformsAddOn(alias)
        : !isTransformsAddOn(alias);
    })
    .filter(({ alias }) => {
      if (filterAI === undefined) {
        return true;
      }

      return filterAI ? isAIAddOn(alias) : !isAIAddOn(alias);
    })
    .filter(({ default_price_per_unit }) => {
      if (filterFree === undefined) {
        return true;
      }

      return filterFree
        ? default_price_per_unit === 0
        : default_price_per_unit > 0;
    });

  return filterTransforms
    ? // basic needs to be before advanced
      filteredAddOns.sort(({ alias: alias1 }, { alias: alias2 }) =>
        alias1 > alias2 ? -1 : 1,
      )
    : filteredAddOns;
}

export function isTransformTier(
  aliasOrSimplifiedAlias: string,
  transformTier: "basic" | "advanced",
) {
  return aliasOrSimplifiedAlias.includes(transformTier);
}

export function getTransformsLabel(aliasOrSimplifiedAlias: string): string {
  if (isTransformTier(aliasOrSimplifiedAlias, "basic")) {
    return "Basic: SQL";
  } else if (isTransformTier(aliasOrSimplifiedAlias, "advanced")) {
    return "Advanced: SQL + Python";
  }

  return "undefined";
}

export function getTransformsLabelShort(
  aliasOrSimplifiedAlias: string,
): string {
  if (isTransformTier(aliasOrSimplifiedAlias, "basic")) {
    return "Basic";
  } else if (isTransformTier(aliasOrSimplifiedAlias, "advanced")) {
    return "Advanced";
  }

  return "undefined";
}
